import { getLocalApiOrigin } from '../localApi';
import { getEffectiveMusicVolume } from './soundSettings';
import { attachUnderwaterFilter, detachUnderwaterFilter, hasUnderwaterFilter } from './underwaterFilter';

let currentAudio = null;
let currentTrack = null;
let fadeInTimer = null;
let fadeOutTimer = null;
// Track all audio objects ever created so we can kill orphans
const allAudios = new Set();
// Current subsurface "far-away" filter amount (0 = no effect, 1 = full
// underground-hall muffle+reverb). Applied to every audio element this
// manager creates so newly started tracks pick up the active state
// without a jump. Driven by setMusicUnderwater.
let underwaterAmount = 0;

const MUSIC_DISABLED = false;
// Multi-source suppression. Each reason string represents an
// independent layer that wants the built-in music silenced (custom
// music playback, subsurface region view, etc.). The music is
// suppressed while the set is non-empty; the last reason removed
// re-allows playback. Keeps independent sources from un-suppressing
// each other.
const suppressors = new Set();
let suppressed = false;
// Transient duck applied on top of the user's music volume while a
// notification plays. Always returns to 1. Anything that seizes `volume`
// mid-duck (a crossfade, a stop) must reset it via `clearDuck()`.
let duckFactor = 1;
let duckTimer = null;
let duckFade = null;

function clearDuck() {
  clearInterval(duckFade);
  clearTimeout(duckTimer);
  duckFade = null;
  duckTimer = null;
  duckFactor = 1;
}

function getTrackUrl(track) {
  const base = getLocalApiOrigin();
  const tracks = {
    'arena-hub': `${base}/game-assets/snd-arena-hub-music.mp3`,
    'arena-store': `${base}/game-assets/snd-arena-store-music.mp3`,
    'arena-deckbuilder': `${base}/game-assets/snd-arena-deckbuilder-music.mp3`,
    'arena-match': `${base}/game-assets/snd-arena-hub-music.mp3`,
  };
  return tracks[track] || null;
}

function easeFade(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function killAllExcept(keepAudio) {
  for (const audio of allAudios) {
    if (audio !== keepAudio) {
      try { audio.pause(); } catch {}
      detachUnderwaterFilter(audio);
      allAudios.delete(audio);
    }
  }
}

// `to` may be a getter. A fade toward the user's level re-reads it every
// tick, so a settings change mid-fade lands on the new level instead of the
// one captured when the fade started.
function fadeAudio(audio, fromVol, to, duration, onDone) {
  const steps = 40;
  const stepTime = duration / steps;
  let step = 0;
  return setInterval(() => {
    step++;
    const toVol = typeof to === 'function' ? to() : to;
    if (step >= steps) {
      audio.volume = toVol;
      if (onDone) onDone();
      return;
    }
    const progress = easeFade(step / steps);
    audio.volume = fromVol + (toVol - fromVol) * progress;
  }, stepTime);
}

export function playMusic(track, { fadeInDuration = 3000 } = {}) {
  if (MUSIC_DISABLED) return;
  if (suppressed) return;
  const targetVolume = getEffectiveMusicVolume();
  if (targetVolume <= 0) return;

  // Already playing this exact track. Do nothing
  if (currentTrack === track && currentAudio && !currentAudio.paused) return;

  // Clear any pending fades
  clearInterval(fadeInTimer);
  clearInterval(fadeOutTimer);
  fadeInTimer = null;
  fadeOutTimer = null;
  clearDuck();

  // Fade out the current track
  const oldAudio = currentAudio;
  if (oldAudio) {
    const oldVol = oldAudio.volume;
    fadeOutTimer = fadeAudio(oldAudio, oldVol, 0, 2000, () => {
      clearInterval(fadeOutTimer);
      fadeOutTimer = null;
      oldAudio.pause();
      detachUnderwaterFilter(oldAudio);
      allAudios.delete(oldAudio);
    });
  }

  // Kill any orphaned audio objects from previous rapid transitions
  const url = getTrackUrl(track);
  if (!url) return;

  const audio = new Audio(url);
  audio.loop = true;
  audio.volume = 0;
  // Required for createMediaElementSource. Without it the audio's
  // texture data is tainted and the Web Audio chain throws on attach.
  audio.crossOrigin = 'anonymous';

  // Wire through the underwater filter chain ONLY when the subsurface
  // effect is live. createMediaElementSource permanently routes the
  // element's media pipeline through Web Audio, which forces full decode
  // buffering (tens of MB per track on iOS Safari); an unfiltered track
  // streams natively for free. If the player is already in subsurface
  // view when this track starts, attach + snap so there's no glitch;
  // otherwise setMusicUnderwater attaches lazily when the effect ramps in.
  if (underwaterAmount > 0) {
    attachUnderwaterFilter(audio).setAmount(underwaterAmount, 0);
  }

  // If the audio file doesn't exist (e.g. copyrighted match music removed),
  // silently give up instead of retrying forever.
  audio.addEventListener('error', () => {
    if (currentAudio === audio) {
      currentAudio = null;
      currentTrack = null;
    }
    detachUnderwaterFilter(audio);
    allAudios.delete(audio);
  });

  // Set as current BEFORE play to prevent race conditions
  currentAudio = audio;
  currentTrack = track;
  // Kill orphans but spare the old audio that's fading out
  for (const a of allAudios) {
    if (a !== audio && a !== oldAudio) {
      try { a.pause(); } catch {}
      allAudios.delete(a);
    }
  }
  allAudios.add(audio);

  audio.play().catch(() => {
    // If the audio source failed to load (404, network error), don't retry.
    if (audio.error) return;

    // Autoplay blocked. Retry on user gesture AND periodically.
    // Desktop CEF sometimes allows play after a short delay even
    // without a visible click (e.g. after initial page load settles).
    let retryTimer = null;
    const resume = () => {
      if (currentAudio !== audio) {
        cleanup();
        return;
      }
      if (audio.paused) {
        audio.play().then(cleanup).catch(() => {});
      } else {
        cleanup();
      }
    };
    const cleanup = () => {
      clearInterval(retryTimer);
      retryTimer = null;
      document.removeEventListener('click', resume);
      document.removeEventListener('keydown', resume);
      document.removeEventListener('pointerdown', resume);
      document.removeEventListener('mousemove', resume);
    };
    retryTimer = setInterval(resume, 500);
    document.addEventListener('click', resume, { once: true });
    document.addEventListener('keydown', resume, { once: true });
    document.addEventListener('pointerdown', resume, { once: true });
    document.addEventListener('mousemove', resume, { once: true });
  });

  // Fade in
  fadeInTimer = fadeAudio(audio, 0, getEffectiveMusicVolume, fadeInDuration, () => {
    clearInterval(fadeInTimer);
    fadeInTimer = null;
  });
}

export function stopMusic(fadeOutDuration = 2000) {
  clearInterval(fadeInTimer);
  fadeInTimer = null;
  clearDuck();

  if (!currentAudio) {
    // Kill any orphans that might still be playing
    killAllExcept(null);
    return;
  }

  const audio = currentAudio;
  currentAudio = null;
  currentTrack = null;

  if (fadeOutDuration <= 0) {
    audio.pause();
    detachUnderwaterFilter(audio);
    allAudios.delete(audio);
    killAllExcept(null);
    return;
  }

  clearInterval(fadeOutTimer);
  const startVol = audio.volume;
  fadeOutTimer = fadeAudio(audio, startVol, 0, fadeOutDuration, () => {
    clearInterval(fadeOutTimer);
    fadeOutTimer = null;
    audio.pause();
    detachUnderwaterFilter(audio);
    allAudios.delete(audio);
    killAllExcept(null);
  });
}

export function updateMusicVolume() {
  if (!currentAudio || currentAudio.paused) return;
  currentAudio.volume = getEffectiveMusicVolume() * duckFactor;
}

/**
 * Dip the track briefly so a notification can land on top of it, then bring
 * it back. Fast down, slow up. See `duckAmbience` for why.
 *
 * Skipped while a crossfade is running: `fadeAudio` owns `volume` for the
 * duration of a track change, and a duck layered on top would be overwritten
 * mid-ramp and leave the track at the wrong level.
 */
export function duckMusic({ depth = 0.35, downMs = 180, holdMs = 900, upMs = 1600 } = {}) {
  if (!currentAudio || currentAudio.paused || fadeInTimer || fadeOutTimer) return;
  const full = getEffectiveMusicVolume();
  if (full <= 0) return;
  clearDuck();
  duckFactor = depth;
  // fadeAudio returns a live interval and does NOT stop itself at the end.
  // every caller here clears its own handle, so the duck has to as well or
  // the ramp would keep firing and re-trigger the restore forever.
  duckFade = fadeAudio(currentAudio, currentAudio.volume, full * depth, downMs, () => {
    clearInterval(duckFade);
    duckFade = null;
    duckTimer = setTimeout(() => {
      duckFactor = 1;
      if (!currentAudio || currentAudio.paused) return;
      duckFade = fadeAudio(currentAudio, currentAudio.volume, getEffectiveMusicVolume, upMs, () => {
        clearInterval(duckFade);
        duckFade = null;
      });
    }, holdMs);
  });
}

export function getCurrentTrack() {
  return currentTrack;
}

/**
 * Add a suppression reason. While any reason is active, `playMusic()`
 * is a no-op and the currently playing track fades out. Each caller
 * is expected to remove its own reason when done. Idempotent.
 */
export function addMusicSuppressor(reason) {
  suppressors.add(reason);
  suppressed = suppressors.size > 0;
  if (suppressed && currentAudio) stopMusic(2000);
}

export function removeMusicSuppressor(reason) {
  suppressors.delete(reason);
  suppressed = suppressors.size > 0;
}

/**
 * Legacy single-flag API. Kept for backwards compatibility. Internally
 * routed through the 'custom-music' suppression reason so the music
 * manager can still distinguish concurrent sources (e.g. subsurface
 * region view) without stomping each other's flag.
 */
export function setMusicSuppressed(value) {
  if (value) addMusicSuppressor('custom-music');
  else removeMusicSuppressor('custom-music');
}

export function isMusicSuppressed() {
  return suppressed;
}

/**
 * Drive the "far away, underground hall" effect on the currently
 * playing music. Keeps playback going but ramps in a lowpass-muffle
 * + convolver reverb so it sounds distant. `amount` runs 0 (clean) to
 * 1 (full subsurface effect). `fadeMs` is the linear ramp duration on
 * the underlying AudioParams. The amount is also stored so that a
 * track started AFTER setMusicUnderwater (e.g. a screen transition
 * while still in subsurface) picks up the active state immediately.
 */
export function setMusicUnderwater(amount, fadeMs = 1500) {
  underwaterAmount = Math.max(0, Math.min(1, amount));
  if (!currentAudio) return;
  // Attach lazily on the first non-zero amount. Ramping to 0 on an
  // unfiltered element needs no chain at all.
  if (underwaterAmount > 0 || hasUnderwaterFilter(currentAudio)) {
    attachUnderwaterFilter(currentAudio).setAmount(underwaterAmount, fadeMs);
  }
}
